import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Spinner, apiClient, cn, copyToClipboard } from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'

type MfaStatus = {
  enabled: boolean
  has_totp: boolean
  email_enabled: boolean
  backup_codes_remaining: number
}

type EnableResp = {
  secret: string
  otpauth_url: string
  qr_data_url: string
}

type ConfirmResp = {
  backup_codes: string[]
}

type RegenerateResp = {
  recovery_codes: string[]
}

export function MfaPage() {
  const { data: status, isLoading, refetch } = useQuery({
    queryKey: ['auth', 'mfa-status'] as const,
    queryFn: () => apiClient.get<MfaStatus>('/gapi/user/auth/2fa/status'),
  })
  const [enable, setEnable] = useState<EnableResp | null>(null)
  const [confirmCode, setConfirmCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onEnable = async () => {
    setBusy(true)
    setError(null)
    try {
      const r = await apiClient.post<EnableResp>('/gapi/user/auth/2fa/enable', {})
      setEnable(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : '启用失败')
    } finally {
      setBusy(false)
    }
  }

  const onConfirm = async () => {
    setBusy(true)
    setError(null)
    try {
      const r = await apiClient.post<ConfirmResp>('/gapi/user/auth/2fa/confirm-enable', { code: confirmCode })
      setBackupCodes(r.backup_codes)
      setEnable(null)
      setConfirmCode('')
      await refetch()
    } catch (e) {
      setError(e instanceof Error ? e.message : '确认失败')
    } finally {
      setBusy(false)
    }
  }

  const onToggleEmail = async () => {
    setBusy(true)
    try {
      await apiClient.post('/gapi/user/auth/2fa/email/toggle', { enabled: !status?.email_enabled })
      await refetch()
    } catch (e) {
      setError(e instanceof Error ? e.message : '切换失败')
    } finally {
      setBusy(false)
    }
  }

  // B4-#4 VwOOZ 2FA 备用码再生成
  //
  // 流程:确认对话框 → POST /gapi/user/auth/2fa/regenerate-recovery-codes →
  //       展示 8 个新明文码(替换旧的)+ 提示用户保存。
  const [showRegenConfirm, setShowRegenConfirm] = useState(false)

  const onRegenerateRecovery = async () => {
    setBusy(true)
    setError(null)
    setShowRegenConfirm(false)
    try {
      const r = await apiClient.post<RegenerateResp>(
        '/gapi/user/auth/2fa/regenerate-recovery-codes',
        {},
      )
      setBackupCodes(r.recovery_codes)
      await refetch()
    } catch (e) {
      setError(e instanceof Error ? e.message : '重新生成失败')
    } finally {
      setBusy(false)
    }
  }

  const onToggleTotp = async () => {
    if (status?.has_totp) {
      setBusy(true)
      try {
        await apiClient.post('/gapi/user/auth/2fa/disable', {})
        await refetch()
      } catch (e) {
        setError(e instanceof Error ? e.message : '关闭失败')
      } finally {
        setBusy(false)
      }
    } else {
      void onEnable()
    }
  }

  return (
    <AppLayout>
      <MobileScreen>
        <TopBar title="两步验证" />

        <main data-testid="qcBV7" className="flex flex-1 flex-col gap-4 px-6 pt-3 pb-12">
          <section className="rounded-2xl bg-[#F6F7F8] p-5 text-center">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#19caad]">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" /></svg>
            </span>
            <p className="mt-2 text-[15px] font-semibold text-[#1A1A1A]">保护您的账户安全</p>
            <p className="mt-1 text-[12px] text-[#9CA3AF]">启用两步验证后，登录时需要输入邮箱验证码</p>
          </section>

          {isLoading ? (
            <div className="flex justify-center py-8"><Spinner className="text-gray-400" /></div>
          ) : (
            <>
              <p className="text-[13px] font-semibold text-[#1A1A1A]">验证方式</p>
              <ul className="flex flex-col gap-2">
                <ToggleListItem
                  icon="email"
                  label="邮箱验证"
                  hint="登录时通过邮箱接收 6 位验证码"
                  value={status?.email_enabled ?? false}
                  onClick={onToggleEmail}
                  disabled={busy}
                />
                <ToggleListItem
                  icon="totp"
                  label="TOTP 验证"
                  hint="Google Authenticator 等"
                  value={status?.has_totp ?? false}
                  onClick={onToggleTotp}
                  disabled={busy}
                />
              </ul>
            </>
          )}

          {error && <p className="text-[13px] text-[#FF6B6B]">{error}</p>}

          {backupCodes.length > 0 && (
            <section className="rounded-2xl bg-[#FEF3C7] p-4 text-[12px] text-[#1A1A1A]">
              <p className="font-semibold">请妥善保存备用码（仅显示一次）</p>
              <ul className="mt-2 grid grid-cols-2 gap-1 font-mono text-[12px]">
                {backupCodes.map((c) => <li key={c}>{c}</li>)}
              </ul>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => void copyToClipboard(backupCodes.join('\n'))}
                  className="rounded-md bg-white px-3 py-1 text-[12px] font-semibold text-[#19caad]"
                >
                  复制全部
                </button>
                <button
                  type="button"
                  onClick={() => setBackupCodes([])}
                  className="rounded-md bg-[#1A1A1A] px-3 py-1 text-[12px] font-semibold text-white"
                >
                  我已保存
                </button>
              </div>
            </section>
          )}

          {/* B4-#4 VwOOZ:已启用 TOTP 时显示"重新生成备用码"入口 */}
          {status?.has_totp && backupCodes.length === 0 && (
            <section className="rounded-2xl bg-[#F6F7F8] p-4">
              <p className="text-[13px] font-semibold text-[#1A1A1A]">备用码</p>
              <p className="mt-1 text-[11px] text-[#9CA3AF]">
                丢失备用码或怀疑泄露时,可重新生成 8 个新码。**旧码立即失效**。
              </p>
              {status.backup_codes_remaining !== undefined && (
                <p className="mt-1 text-[11px] text-[#9CA3AF]">
                  剩余可用:{status.backup_codes_remaining} 个
                </p>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => setShowRegenConfirm(true)}
                className="mt-3 h-10 w-full rounded-lg bg-[#1971ca] text-[13px] font-semibold text-white disabled:opacity-50"
              >
                重新生成备用码
              </button>
            </section>
          )}

          {showRegenConfirm && (
            <div
              role="dialog"
              aria-modal="true"
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
              onClick={() => setShowRegenConfirm(false)}
            >
              <div
                className="w-full max-w-[320px] rounded-2xl bg-white p-5"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-[15px] font-semibold text-[#1A1A1A]">确认重新生成?</h3>
                <p className="mt-2 text-[12px] text-[#9CA3AF]">
                  生成后,**当前所有备用码立即失效**。请确保新码会被妥善保存。
                </p>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowRegenConfirm(false)}
                    className="h-10 flex-1 rounded-lg bg-[#F1F2F4] text-[13px] font-semibold text-[#1A1A1A]"
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={onRegenerateRecovery}
                    disabled={busy}
                    className="h-10 flex-1 rounded-lg bg-[#FF6B6B] text-[13px] font-semibold text-white disabled:opacity-50"
                  >
                    确认生成
                  </button>
                </div>
              </div>
            </div>
          )}

          {enable && (
            <section className="rounded-2xl bg-white p-4 text-[13px] shadow-[0_8px_24px_rgba(0,0,0,0.06)]">
              <p className="mb-2 font-semibold text-[#1A1A1A]">用 Authenticator 扫码：</p>
              <img src={enable.qr_data_url} alt="TOTP QR" className="mx-auto max-w-[180px]" />
              <p className="mt-2 break-all text-[11px] text-[#9CA3AF]">手动密钥：{enable.secret}</p>
              <input
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                placeholder="6 位 TOTP 验证码"
                maxLength={6}
                inputMode="numeric"
                className="mt-3 h-11 w-full rounded-lg bg-[#F6F7F8] px-4 text-[14px] outline-none placeholder:text-[#B4B7BC]"
              />
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => setEnable(null)}
                  className="h-10 flex-1 rounded-lg bg-[#F1F2F4] text-[13px] font-semibold text-[#1A1A1A]"
                >
                  取消
                </button>
                <button
                  onClick={onConfirm}
                  disabled={busy || confirmCode.length !== 6}
                  className="h-10 flex-1 rounded-lg bg-[#19caad] text-[13px] font-semibold text-white disabled:opacity-50"
                >
                  确认
                </button>
              </div>
            </section>
          )}
        </main>
      </MobileScreen>
    </AppLayout>
  )
}

function ToggleListItem({
  icon,
  label,
  hint,
  value,
  onClick,
  disabled,
}: {
  icon: 'email' | 'totp'
  label: string
  hint: string
  value: boolean
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <li className="flex items-center gap-3 rounded-2xl bg-[#F6F7F8] px-4 py-3">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#9CA3AF]">
        {icon === 'email' ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 8l9 6 9-6" /></svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="2" /><path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-medium text-[#1A1A1A]">{label}</p>
        <p className="text-[11px] text-[#9CA3AF]">{hint}</p>
      </div>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-pressed={value}
        className={cn(
          'relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-50',
          value ? 'bg-[#19caad]' : 'bg-[#E0E0E0]',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform',
            value ? 'translate-x-[22px]' : 'translate-x-0.5',
          )}
        />
      </button>
    </li>
  )
}
